export type WorkflowBranchPageShape = {
  status?: string;
  coverage?: "warming" | "ready";
  retryAfterSeconds?: number;
  cursor?: string | null;
  hasMore?: boolean;
};

export function workflowStepBranchRequestEnabled({
  hasSession,
  runId,
  expanded,
}: {
  hasSession: boolean;
  runId: string;
  expanded: boolean;
}): boolean {
  return hasSession && !!runId && expanded;
}

export function workflowStepBranchNextCursor(
  page: WorkflowBranchPageShape | undefined,
  consumedCursors: readonly (string | undefined)[],
): string | undefined {
  const cursor = page?.hasMore && page.cursor?.trim() ? page.cursor : undefined;
  return cursor !== undefined && !consumedCursors.includes(cursor) ? cursor : undefined;
}

export function workflowStepBranchHasRepeatedCursor(
  page: WorkflowBranchPageShape | undefined,
  consumedCursors: readonly (string | undefined)[],
): boolean {
  const cursor = page?.hasMore && page.cursor?.trim() ? page.cursor : undefined;
  return cursor !== undefined && consumedCursors.includes(cursor);
}

export function workflowStepBranchPollingEnabled({ active, warming }: { active: boolean; warming: boolean }): boolean {
  return active || warming;
}

export function waitForWorkflowStepBranchRetry(
  signal: AbortSignal,
  delayMs: number,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(signal.reason ?? new Error("Workflow branch request aborted"));
      return;
    }
    let timer: ReturnType<typeof setTimeout>;
    const onAbort = () => {
      clearTimeout(timer);
      signal.removeEventListener("abort", onAbort);
      reject(signal.reason ?? new Error("Workflow branch request aborted"));
    };
    timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, delayMs);
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

export async function fetchWorkflowStepBranchPageUntilReady<TPage extends WorkflowBranchPageShape>(
  fetchPage: (signal: AbortSignal) => Promise<TPage>,
  waitForRetry: (retryAfterSeconds: number | undefined, signal: AbortSignal) => Promise<void>,
  signal: AbortSignal,
): Promise<TPage> {
  let page = await fetchPage(signal);
  while (page.status === "warming") {
    await waitForRetry(page.retryAfterSeconds, signal);
    page = await fetchPage(signal);
  }
  return page;
}

export async function fetchWorkflowStepBranchPagesFromHead<TPage extends WorkflowBranchPageShape>(
  fetchPage: (cursor: string | undefined, signal: AbortSignal) => Promise<TPage>,
  pageCount: number,
  waitForRetry: (retryAfterSeconds: number | undefined, signal: AbortSignal) => Promise<void>,
  signal: AbortSignal,
): Promise<{ pages: TPage[]; pageParams: (string | undefined)[] }> {
  const pages: TPage[] = [];
  const pageParams: (string | undefined)[] = [];
  let cursor: string | undefined;
  for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
    const pageParam = cursor;
    const page = await fetchWorkflowStepBranchPageUntilReady(
      (requestSignal) => fetchPage(pageParam, requestSignal),
      waitForRetry,
      signal,
    );
    pages.push(page);
    pageParams.push(pageParam);
    cursor = page.hasMore && page.cursor?.trim() ? page.cursor : undefined;
    if (cursor === undefined) break;
  }
  return { pages, pageParams };
}
